import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Mail, MapPin } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";

export const useSettings = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch((e) => logError("Failed to load settings", e));
  }, []);
  return s;
};

export const waLink = (number, text) =>
  `https://wa.me/${(number || "").replace(/\D/g, "")}?text=${encodeURIComponent(text || "")}`;

export const SiteLayout = ({ children }) => {
  const { t, lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const s = useSettings();
  const loc = useLocation();
  const nav = [
    { to: "/", label: t("home") },
    { to: "/book", label: t("bookNow") },
    { to: "/packages", label: t("packages") },
    { to: "/check-booking", label: t("checkBooking") },
    { to: "/faq", label: t("faq") },
    { to: "/contact", label: t("contact") },
    { to: "/member", label: t("memberArea") },
  ];
  return (
    <div className="min-h-screen bg-cream">
      <header className="sticky top-0 z-50 glass">
        <div className="mx-auto max-w-6xl px-5 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="font-display text-xl tracking-tight">
            Lapak<span className="text-lm-red">Mahjong</span><span className="text-lm-gold">.id</span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm">
            {nav.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                className={`hover:text-lm-red ${loc.pathname === n.to ? "text-lm-red" : "text-ink-soft"}`}>{n.label}</Link>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button data-testid="lang-toggle" onClick={() => setLang(lang === "en" ? "id" : "en")}
              className="text-xs border border-black/15 rounded-full px-3 py-1 hover:border-lm-red">
              {lang === "en" ? "EN" : "ID"}
            </button>
            <button className="md:hidden" data-testid="mobile-menu-btn" onClick={() => setOpen(!open)}>
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-black/5 bg-cream px-5 py-3 space-y-3">
            {nav.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                data-testid={`mnav-${n.to.replace("/", "") || "home"}`} className="block text-ink-soft">{n.label}</Link>
            ))}
          </div>
        )}
      </header>
      {children}
      <footer className="mt-24 bg-ink text-cream/80">
        <div className="mx-auto max-w-6xl px-5 py-14 grid gap-10 md:grid-cols-3">
          <div>
            <div className="font-display text-2xl text-cream">Lapak<span className="text-lm-red">Mahjong</span>.id</div>
            <p className="mt-3 text-sm max-w-xs">{t("heroSub")}</p>
          </div>
          <div className="text-sm space-y-2">
            {nav.map((n) => <Link key={n.to} to={n.to} className="block hover:text-lm-gold">{n.label}</Link>)}
            <Link to="/syarat-dan-ketentuan" data-testid="footer-terms"
              className="block hover:text-lm-gold">{t("rentalTerms")}</Link>
          </div>
          <div className="text-sm space-y-2">
            <div className="flex items-center gap-2"><Instagram size={15} /> {s?.instagram}</div>
            <div className="flex items-center gap-2"><Mail size={15} /> {s?.email}</div>
            {s?.pickup_maps_link ? (
              <a href={s.pickup_maps_link} target="_blank" rel="noreferrer" className="flex items-start gap-2 hover:text-lm-gold">
                <MapPin size={15} className="mt-0.5 shrink-0" /> {s?.pickup_address || s?.address}
              </a>
            ) : (
              <div className="flex items-start gap-2"><MapPin size={15} className="mt-0.5 shrink-0" /> {s?.address}</div>
            )}
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs">© {new Date().getFullYear()} LapakMahjong.id</div>
      </footer>
    </div>
  );
};
